import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, ArrowRight, ChartLineUp, Scales, Eye, Handshake } from "@phosphor-icons/react";
import api from "../lib/api";
import CompetitionCard from "../components/CompetitionCard";
import BudgetModule from "../components/BudgetModule";
import SEO from "../components/SEO";
import { toast } from "sonner";
import { isCatalogueAbort } from "../lib/publicCatalogue";
import {
  fetchHomeCatalogue,
  readHomeCatalogue,
  writeHomeCatalogue,
} from "../lib/publicHomeCatalogue";

const ExplainerCard = ({ icon: Icon, title, body, accent }) => (
  <div className="rounded-lg border border-white/10 p-6 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
    <div
      className="h-10 w-10 rounded-md flex items-center justify-center mb-4"
      style={{ background: accent, color: "var(--cc-navy)" }}
    >
      <Icon size={20} weight="duotone" />
    </div>
    <h3 className="font-display text-lg font-semibold mb-2 text-white">{title}</h3>
    <p className="text-sm text-[var(--cc-text-muted)] leading-relaxed">{body}</p>
  </div>
);

const emptyHomeCatalogue = () => ({
  snapshotId: null,
  publishedAt: null,
  featured: [],
  bestValue: [],
  closingSoon: [],
});

const homeCatalogueFromResult = (result) => ({
  snapshotId: result.snapshotId,
  publishedAt: result.publishedAt,
  featured: result.rankings.overall.page.items.slice(0, 4),
  bestValue: result.rankings.value.page.items.slice(0, 4),
  closingSoon: result.rankings.closing_soon.page.items.slice(0, 4),
});

const verifiedTime = (value) => {
  if (!value) return "an earlier verified publication";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "an earlier verified publication";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export default function Home() {
  const [homeCatalogue, setHomeCatalogue] = useState(() => {
    const cached = readHomeCatalogue();
    return cached ? homeCatalogueFromResult(cached) : emptyHomeCatalogue();
  });
  const homeCatalogueRef = useRef(homeCatalogue);
  const [cataloguePhase, setCataloguePhase] = useState(
    homeCatalogue.snapshotId ? "checking" : "loading",
  );
  const catalogueRequest = useRef({ generation: 0, controller: null });
  const [q, setQ] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const commitHomeCatalogue = useCallback((next) => {
    homeCatalogueRef.current = next;
    setHomeCatalogue(next);
  }, []);

  const refreshHomeCatalogue = useCallback(() => {
    const generation = catalogueRequest.current.generation + 1;
    catalogueRequest.current.controller?.abort();
    const controller = new AbortController();
    catalogueRequest.current = { generation, controller };
    const cached = readHomeCatalogue();
    setCataloguePhase(homeCatalogueRef.current.snapshotId ? "checking" : "loading");

    fetchHomeCatalogue({ signal: controller.signal, cached })
      .then((result) => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        commitHomeCatalogue(homeCatalogueFromResult(result));
        writeHomeCatalogue(result);
        setCataloguePhase("ready");
      })
      .catch((requestError) => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        if (isCatalogueAbort(requestError)) return;
        setCataloguePhase(homeCatalogueRef.current.snapshotId ? "stale" : "error");
      })
      .finally(() => {
        if (controller.signal.aborted || catalogueRequest.current.generation !== generation) return;
        catalogueRequest.current.controller = null;
      });
    return controller;
  }, [commitHomeCatalogue]);

  useEffect(() => {
    const controller = refreshHomeCatalogue();
    return () => controller.abort();
  }, [refreshHomeCatalogue]);

  useEffect(() => {
    const refreshWhenOnline = () => refreshHomeCatalogue();
    window.addEventListener("online", refreshWhenOnline);
    return () => window.removeEventListener("online", refreshWhenOnline);
  }, [refreshHomeCatalogue]);

  const {
    snapshotId,
    publishedAt,
    closingSoon,
    bestValue,
    featured,
  } = homeCatalogue;
  const hasHomeCatalogue = Boolean(snapshotId);
  const catalogueStatus = (() => {
    if (cataloguePhase === "loading") return "Loading the latest competitions…";
    if (cataloguePhase === "error") return "We couldn’t load the latest competitions. Please try again.";
    if (cataloguePhase === "checking") {
      return "Checking for the latest competitions. You can keep browsing.";
    }
    if (cataloguePhase === "stale") {
      return `Showing earlier competitions from ${verifiedTime(publishedAt)}. We couldn’t check for updates.`;
    }
    return `Showing verified comparisons from ${verifiedTime(publishedAt)}.`;
  })();

  const handleSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    window.location.href = `/competitions?q=${encodeURIComponent(q.trim())}`;
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/email-signup", { email, source: "homepage" });
      toast.success(data.already_subscribed ? "You're already on the list." : "Subscribed. We'll send the best value picks weekly.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Competition Checker | Compare UK Competitions & Odds"
        description="Compare live UK competitions across cars, cash, bikes, tech and more. Check ticket prices, ticket caps, odds and operator details before you enter."
        path="/"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://competitionchecker.com/#organization",
              name: "Competition Checker",
              legalName: "QUOD-Tech Ltd",
              url: "https://competitionchecker.com/",
              logo: "https://competitionchecker.com/android-chrome-512x512.png",
            },
            {
              "@type": "WebSite",
              "@id": "https://competitionchecker.com/#website",
              name: "Competition Checker",
              url: "https://competitionchecker.com/",
              publisher: { "@id": "https://competitionchecker.com/#organization" },
              description:
                "Independent UK prize competition odds, value and transparency comparison.",
              potentialAction: {
                "@type": "SearchAction",
                target: "https://competitionchecker.com/competitions?q={search_term_string}",
                "query-input": "required name=search_term_string",
              },
            },
          ],
        }}
        jsonLdId="home"
      />
      {/* HERO */}
      <section className="relative grain-bg overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/4d3b6f2b-59f0-4c01-a01c-84213c5b11bf/images/644bcf33b6aaae12790577a367a549eecfdc761e6bd73bed6124f5df89ceddc1.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.55,
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[var(--cc-navy)]/30 via-[var(--cc-navy)]/70 to-[var(--cc-navy)]" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-28 sm:pt-28 sm:pb-36">
          <div className="max-w-3xl">
            <div className="overline text-[var(--cc-gold)] mb-5" data-testid="hero-overline">
              UK Prize Competition Transparency
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Compare UK Competitions<br />
              <span className="text-[var(--cc-gold)]">Before You Enter</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[var(--cc-text-muted)] max-w-2xl leading-relaxed">
              Check the real odds, ticket prices and ticket caps across UK competition
              sites, all in one place.
            </p>
            <p className="mt-3 text-sm sm:text-base text-[var(--cc-text-muted)] max-w-2xl leading-relaxed">
              We independently aggregate public competition information and calculate
              odds only when a trusted total ticket cap is available. When it is not,
              we say so rather than inventing a number. Check the real odds before you enter.
            </p>

            <nav className="mt-6 flex flex-wrap gap-x-5 gap-y-3 text-sm" aria-label="Explore competition comparisons">
              <Link className="text-[var(--cc-gold)] hover:underline" to="/competitions">Browse competitions</Link>
              <Link className="text-[var(--cc-gold)] hover:underline" to="/uk-competitions">UK competition guide</Link>
              <Link className="text-[var(--cc-gold)] hover:underline" to="/uk-car-competitions">Car competitions</Link>
              <Link className="text-[var(--cc-gold)] hover:underline" to="/operators">Competition operators</Link>
            </nav>

            <form
              onSubmit={handleSearch}
              data-testid="hero-search-form"
              className="mt-8 flex items-center gap-2 max-w-xl bg-white rounded-md p-1.5 border border-white/20"
            >
              <MagnifyingGlass size={18} className="ml-3 text-[var(--cc-text-dark-muted)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="hero-search-input"
                placeholder="Search cars, bikes, watches, cash…"
                className="flex-1 bg-transparent text-[var(--cc-text-dark)] outline-none text-sm py-2 px-1"
              />
              <button
                type="submit"
                data-testid="hero-search-submit"
                className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-1.5"
              >
                Compare <ArrowRight size={14} weight="bold" />
              </button>
            </form>

            <aside
              className="mt-6 flex flex-col items-start gap-3 rounded-lg border border-[var(--cc-gold)]/30 bg-[var(--cc-navy)]/80 p-4 sm:flex-row sm:items-center sm:justify-between"
              aria-label="Competition Checker on your mobile"
            >
              <div>
                <p className="font-semibold text-white">Competition Checker, one tap away</p>
                <p className="mt-1 text-sm text-[var(--cc-text-muted)]">Add it to your Android, iPhone or iPad home screen.</p>
              </div>
              <Link
                to="/guides/install-competition-checker"
                data-testid="home-get-app-link"
                className="btn-gold inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--cc-gold)]"
              >
                Get the app on your mobile <ArrowRight size={16} weight="bold" aria-hidden="true" />
              </Link>
            </aside>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-[var(--cc-text-muted)]">
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[var(--cc-gold)]" /> No account needed to browse. No tickets sold here.</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Plain-English breakdowns</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-white/40" /> Free-entry routes highlighted</div>
            </div>
          </div>
        </div>
      </section>

      {/* BUDGET MODULE — killer feature */}
      <BudgetModule />

      <div
        className="max-w-7xl mx-auto px-6 lg:px-8 pt-10"
        aria-busy={cataloguePhase === "loading" || cataloguePhase === "checking"}
      >
        <p
          className="text-sm text-[var(--cc-text-muted)]"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="home-catalogue-status"
        >
          {catalogueStatus}
        </p>
        {cataloguePhase === "stale" && (
          <button
            type="button"
            onClick={refreshHomeCatalogue}
            className="mt-3 min-h-11 rounded border border-white/20 px-4 py-2 text-sm text-[var(--cc-gold)] hover:border-[var(--cc-gold)]"
            data-testid="home-catalogue-refresh"
          >
            Check again
          </button>
        )}
        {cataloguePhase === "error" && !hasHomeCatalogue && (
          <div
            className="mt-5 rounded-lg border border-amber-300/30 bg-amber-300/5 p-6"
            role="alert"
            data-testid="home-catalogue-error"
          >
            <p className="font-semibold">We couldn’t load the latest competitions. Please try again.</p>
            <p className="mt-2 text-sm text-[var(--cc-text-muted)]">
              Check your connection, then try again. This page will also retry when you reconnect.
            </p>
            <button
              type="button"
              onClick={refreshHomeCatalogue}
              className="mt-4 min-h-11 rounded border border-[var(--cc-gold)] px-5 py-2 text-sm text-[var(--cc-gold)]"
              data-testid="home-catalogue-retry"
            >
              Retry comparisons
            </button>
          </div>
        )}
        {cataloguePhase === "loading" && !hasHomeCatalogue && (
          <div
            className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="home-catalogue-skeletons"
            aria-hidden="true"
          >
            {Array.from({ length: 4 }, (_, index) => (
              <div
                key={index}
                className="h-80 animate-pulse rounded-lg border border-white/10 bg-white/[0.04]"
              />
            ))}
          </div>
        )}
      </div>

      {/* FEATURED */}
      {featured.length > 0 && (
        <Section
          title="Featured comparisons"
          kicker="Overall CompCheck score"
          cta={{ label: "All competitions", to: "/competitions" }}
          items={featured}
          snapshotId={snapshotId}
        />
      )}

      {/* EXPLAINER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline text-[var(--cc-gold)] mb-3">How we score</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight">
              Four numbers,<br /> one honest verdict.
            </h2>
            <p className="mt-5 text-[var(--cc-text-muted)] leading-relaxed">
              Every competition is scored on four pillars plus urgency. Scoring is
              deterministic and explainable — if data is missing, we tell you and
              reduce confidence rather than hide it.
            </p>
            <Link
              to="/how-we-score"
              data-testid="explainer-cta"
              className="inline-flex items-center gap-2 mt-6 text-[var(--cc-gold)] hover:underline text-sm"
            >
              Read the scoring methodology <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <ExplainerCard icon={ChartLineUp} title="Odds" accent="#c5a059" body="Based on the maximum ticket pool. Smaller pools score higher. Missing caps reduce confidence." />
            <ExplainerCard icon={Scales} title="Value" accent="#c5a059" body="Prize value vs ticket cost across the full pool. Expected value per ticket drives the score." />
            <ExplainerCard icon={Eye} title="Transparency" accent="#10b981" body="Clarity of free entry route, extension policy, draw date and ticket cap. Vague = lower score." />
            <ExplainerCard icon={Handshake} title="Trust" accent="#10b981" body="Operator signals: winner visibility, terms clarity, free-entry clarity, extension policy." />
          </div>
        </div>
      </section>

      <section
        className="border-y border-white/10 bg-white/[0.015]"
        data-testid="home-editorial-overview"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="max-w-3xl">
            <div className="overline text-[var(--cc-gold)] mb-3">Independent information</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold">
              Compare the evidence, not just the headline prize.
            </h2>
            <p className="mt-5 text-[var(--cc-text-muted)] leading-relaxed">
              Competition Checker is an independent UK information and comparison
              service. We do not operate competitions, sell tickets, process
              entries or award prizes. We turn public operator evidence into
              consistent comparisons and show where important inputs are missing.
            </p>
          </div>

          <div className="mt-10 grid gap-x-10 gap-y-8 md:grid-cols-2">
            <section className="border-t border-white/10 pt-5">
              <h3 className="font-display text-xl font-semibold">How the data is checked</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
                Ticket price, ticket cap, draw date, image and source identity are
                accepted only under operator-specific evidence rules. A per-user
                purchase limit is never promoted into a total ticket pool, and a
                degraded source does not silently erase the last-known-good public catalogue.
              </p>
              <Link className="mt-3 inline-flex text-sm text-[var(--cc-gold)] hover:underline" to="/data-accuracy">
                Read about data accuracy
              </Link>
            </section>
            <section className="border-t border-white/10 pt-5">
              <h3 className="font-display text-xl font-semibold">What odds unavailable means</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
                Reliable odds need a trusted competition-wide ticket allocation.
                If that denominator is missing, unclear or unsupported by the draw
                mechanic, we keep the competition useful and explain the gap instead
                of publishing a guessed number.
              </p>
              <Link className="mt-3 inline-flex text-sm text-[var(--cc-gold)] hover:underline" to="/guides/how-competition-odds-are-calculated">
                Understand competition odds
              </Link>
            </section>
            <section className="border-t border-white/10 pt-5">
              <h3 className="font-display text-xl font-semibold">Price, cap and value</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
                A cheap ticket can sit inside a very large pool. We compare the
                current trusted unit price with the maximum allocation and keep
                direct cash, included cash, vouchers and explicit cash alternatives separate.
              </p>
              <Link className="mt-3 inline-flex text-sm text-[var(--cc-gold)] hover:underline" to="/guides/ticket-price-versus-ticket-cap">
                Compare ticket price and cap
              </Link>
            </section>
            <section className="border-t border-white/10 pt-5">
              <h3 className="font-display text-xl font-semibold">Verify before entering</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--cc-text-muted)]">
                Operator prices, availability and terms can change. Use our analysis
                to compare, then check the live operator page, official terms and
                free-entry route immediately before deciding whether to enter.
              </p>
              <Link className="mt-3 inline-flex text-sm text-[var(--cc-gold)] hover:underline" to="/guides">
                Browse practical guides
              </Link>
            </section>
          </div>
        </div>
      </section>

      {/* CLOSING SOON */}
      {closingSoon.length > 0 && (
        <Section
          title="Closing soon"
          kicker="Time-sensitive draws"
          cta={{ label: "View all", to: "/competitions?sort=closing_soon" }}
          items={closingSoon}
          snapshotId={snapshotId}
        />
      )}

      {/* BEST VALUE */}
      {bestValue.length > 0 && (
        <Section
          title="Best value right now"
          kicker="Highest value score"
          cta={{ label: "Best value page", to: "/best-value" }}
          items={bestValue}
          snapshotId={snapshotId}
        />
      )}

      {/* EMAIL SIGNUP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="rounded-xl border border-[var(--cc-gold)]/30 bg-gradient-to-br from-[var(--cc-card-dark)] to-[var(--cc-navy)] p-8 sm:p-12 grain-bg">
          <div className="relative z-10 max-w-2xl">
            <div className="overline text-[var(--cc-gold)] mb-3">Weekly</div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
              The best value UK competitions, in your inbox.
            </h2>
            <p className="mt-4 text-[var(--cc-text-muted)]">
              One email a week. Top-scoring competitions by odds, value and transparency. No spam. Unsubscribe anytime.
            </p>
            <form onSubmit={handleSubscribe} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-lg" data-testid="email-signup-form">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                data-testid="email-signup-input"
                placeholder="you@example.co.uk"
                className="flex-1 bg-white/5 border border-white/20 rounded px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-[var(--cc-gold)] outline-none"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="email-signup-submit"
                className="btn-gold px-5 py-3 rounded text-sm disabled:opacity-60"
              >
                {submitting ? "Subscribing..." : "Send me the picks"}
              </button>
            </form>
            <p className="mt-3 text-xs text-[var(--cc-text-muted)]">
              Competition Checker is independent. We never operate competitions or sell tickets.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const Section = ({ title, kicker, cta, items, snapshotId }) => (
  <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
    <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
      <div>
        <div className="overline text-[var(--cc-gold)] mb-2">{kicker}</div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold">{title}</h2>
      </div>
      {cta && (
        <Link
          to={cta.to}
          data-testid={`section-cta-${title.toLowerCase().replace(/\s/g, "-")}`}
          className="text-sm text-white/70 hover:text-[var(--cc-gold)] inline-flex items-center gap-1.5"
        >
          {cta.label} <ArrowRight size={14} />
        </Link>
      )}
    </div>
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((c, i) => (
        <CompetitionCard key={c.id} comp={c} index={i} snapshotId={snapshotId} />
      ))}
    </div>
  </section>
);
